import { useEffect, useMemo, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { getMe, listJobs, logout, type AuthUser } from "./api";
import { Tour, startTour } from "./lib/tour";
import { useTheme } from "./lib/theme";
import { Login } from "./components/Login";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { FooterQuote } from "./components/FooterQuote";
import { Toaster } from "./components/Toaster";
import { isMuted, playStartup, setMuted, setMusicHooks, stopVoice } from "./lib/voice";
import { MusicProvider, useMusic, type MusicMode } from "./lib/music";
import { CommandPalette, openCommandPalette } from "./components/CommandPalette";
import { HowToChatbot } from "./components/HowToChatbot";
import { DownloadToaster } from "./lib/downloadToast";
import { DamonBarks, dispatchDamonBark } from "./components/DamonBarks";
import {
  DailyBriefOverlay,
  DAILY_BRIEF_SEEN_KEY,
  todayInNJ,
  dailyBriefAlreadySeenToday,
} from "./components/DailyBriefOverlay";
import type { JobRow, ServerEvent } from "./types";

// "Stale" = the app was last interacted with more than this many hours ago.
// On a fresh session that long after the previous one, we route the user
// back to /lookup rather than dumping them into whatever page they had
// open last (which is rarely what they want first thing in the morning).
const STALE_HOURS = 12;
const LAST_ACTIVITY_KEY = "lastActivity";

function readLastActivity(): number | null {
  try {
    const v = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeLastActivity(): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  } catch {}
}

export function App() {
  const [user, setUser] = useState<AuthUser | null | false>(null);

  useEffect(() => {
    getMe().then((u) => setUser(u ?? false));
  }, []);

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-[color:var(--color-text-dim)]">
        Loading…
      </div>
    );
  }
  return (
    <MusicProvider>
      <DamonBarks />
      {user === false ? (
        <>
          <Login
            onSuccess={(u) => {
              // FORCE the full intro experience on every login: brief +
              // music interstitial fire fresh, and the staleness check
              // in AuthedShell redirects to /lookup. The cleanest way to
              // get all of that without any "did they just log in?"
              // plumbing is to wipe the three localStorage keys that
              // those overlays read for their show-once gates.
              try {
                localStorage.removeItem("lastActivity");
                localStorage.removeItem("njrc.dailyBriefSeen");
                localStorage.removeItem("interstitialShownAt");
              } catch {}
              window.dispatchEvent(new CustomEvent("damon:login-success"));
              setUser(u);
            }}
          />
          <Toaster />
        </>
      ) : (
        <BriefGate user={user} onLogout={() => setUser(false)} />
      )}
    </MusicProvider>
  );
}

/**
 * Decides what root to render for an authed user: either the morning
 * Daily Brief screen OR the normal AuthedShell. They're SIBLINGS, not
 * stacked - when the brief is showing, AuthedShell isn't mounted at
 * all, so there's no chance of the underlying app peeking through.
 *
 * On Continue: brief un-mounts, AuthedShell mounts fresh, and the
 * existing .fade-in on <main> fires - so the landing page does its
 * usual reveal animation as the user crosses into the app.
 */
function BriefGate({ user, onLogout }: AuthedProps) {
  const [showBrief, setShowBrief] = useState<boolean>(() => !dailyBriefAlreadySeenToday());

  const dismissBrief = () => {
    try {
      localStorage.setItem(DAILY_BRIEF_SEEN_KEY, todayInNJ());
    } catch { /* ignore */ }
    setShowBrief(false);
  };

  if (showBrief) {
    return <DailyBriefOverlay onDismiss={dismissBrief} />;
  }
  return <AuthedShell user={user} onLogout={onLogout} />;
}

interface AuthedProps {
  user: AuthUser;
  onLogout: () => void;
}

function AuthedShell({ user, onLogout }: AuthedProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const music = useMusic();
  // Lightweight job count for header badge - refresh on SSE.
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [connected, setConnected] = useState(false);
  const [muted, setMutedState] = useState<boolean>(() => isMuted());
  // Global NJRC AI assistant - opened via the nav orb, ⌘J / Ctrl+J,
  // or the "njrc:open-assistant" custom event. Same chatbot as /how-to
  // but always available across the app.
  const [assistantOpen, setAssistantOpen] = useState(false);
  // Optional topic the assistant should be primed on when opened from
  // a page's "?" handle. The HowToChatbot uses contextTopicId to send
  // the full topic body as additional context to the model.
  const [assistantTopicId, setAssistantTopicId] = useState<string | undefined>(undefined);
  const [assistantInitialQuery, setAssistantInitialQuery] = useState<string | undefined>(undefined);

  // Global ⌘J / Ctrl+J shortcut for Jack. Mnemonic ("J for Jack")
  // pairs with ⌘K (command palette) so power users have the two
  // most-frequent overlay openings under their fingers without
  // moving from the keyboard. Skips when an input has focus to avoid
  // hijacking text editing in form fields. Also fires on a custom
  // event so any UI element can request the assistant by dispatching
  // window.dispatchEvent(new CustomEvent("njrc:open-assistant", {
  //   detail: { topicId?: string, query?: string }
  // })).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        const t = e.target as HTMLElement | null;
        const inField =
          t?.tagName === "INPUT" ||
          t?.tagName === "TEXTAREA" ||
          t?.isContentEditable;
        if (inField) return;
        e.preventDefault();
        setAssistantTopicId(undefined);
        setAssistantInitialQuery(undefined);
        setAssistantOpen(true);
      }
    }
    function onOpenEvent(e: Event) {
      const detail = (e as CustomEvent<{ topicId?: string; query?: string }>).detail;
      setAssistantTopicId(detail?.topicId);
      setAssistantInitialQuery(detail?.query);
      setAssistantOpen(true);
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("njrc:open-assistant", onOpenEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("njrc:open-assistant", onOpenEvent);
    };
  }, []);

  // Wire voice → music ducking. setMusicHooks is install-once-and-forget;
  // voice.ts calls duck() before each Shao Kahn clip and unduck() after.
  useEffect(() => {
    setMusicHooks({ duck: music.duck, unduck: music.unduck });
    return () => setMusicHooks(null);
  }, [music.duck, music.unduck]);

  // (The morning Daily Brief takeover lives in <BriefGate> - see
  // above. By the time AuthedShell mounts, the brief has already been
  // dismissed or wasn't going to show today; the music + Shao Kahn
  // setup happened in the gate, not here.)

  // Stale-session redirect: if the user is opening the app after an idle
  // gap (e.g. left a tab open over the weekend, refreshed Monday morning),
  // route them back to /today rather than re-mounting whatever deep page
  // they were last on. Today is the new home/triage page (was /lookup
  // before /today existed). Only runs ONCE on mount.
  useEffect(() => {
    const last = readLastActivity();
    const stale = !last || Date.now() - last > STALE_HOURS * 60 * 60 * 1000;
    if (stale && location.pathname !== "/today") {
      navigate("/today", { replace: true });
    }
    writeLastActivity();
    // Keep the activity timestamp fresh on every navigation while the app
    // is open - so an active session doesn't get classified as stale.
    const onClick = () => writeLastActivity();
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh the activity stamp on every route change too.
  useEffect(() => {
    writeLastActivity();
  }, [location.pathname]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
    if (next) stopVoice();
    else playStartup();
  };

  useEffect(() => {
    listJobs().then(setJobs).catch(console.error);
    const es = new EventSource("/events");
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);
    es.onmessage = (msg) => {
      const evt = JSON.parse(msg.data) as ServerEvent;
      if (evt.type === "job_created") {
        setJobs((prev) => [evt.job, ...prev.filter((j) => j.id !== evt.job.id)]);
      } else if (evt.type === "job_updated") {
        setJobs((prev) => prev.map((j) => (j.id === evt.job.id ? evt.job : j)));
      } else if (evt.type === "job_deleted") {
        setJobs((prev) => prev.filter((j) => j.id !== evt.jobId));
      }
    };
    // Note: ⌘K / "/" is now handled by the global <CommandPalette> mounted
    // below. The previous shortcut-to-/records-search behavior is reachable
    // via the palette's "Browse all people" action.
    return () => {
      es.close();
    };
  }, []);

  const activeCount = useMemo(
    () =>
      jobs.filter(
        (j) => j.status === "running" || j.status === "queued" || j.status === "waiting_captcha",
      ).length,
    [jobs],
  );
  const handleLogout = async () => {
    try { await logout(); } catch {}
    dispatchDamonBark("logout");
    onLogout();
  };

  // ─── Tour stats - drive the prominence of the "Take tour" button.
  // Until the user has run the tour ~24 times, show it prominently in
  // the top nav. After that, it's a quiet text link only available in
  // the More menu and on the How-To page.
  const tourStats = (() => {
    try {
      return {
        completedAt: localStorage.getItem("njrc.tour.completedAt"),
        runCount: parseInt(localStorage.getItem("njrc.tour.runCount") ?? "0", 10) || 0,
      };
    } catch {
      return { completedAt: null, runCount: 0 };
    }
  })();
  const tourProminent = tourStats.runCount < 24;

  return (
    <div className="app-container min-h-full">
      <header className="sticky top-0 z-30 border-b border-[color:var(--color-line)] bg-[color:var(--color-bg)]/90 backdrop-blur-md">
        {/* Editorial-grade nav: italic Fraunces verbs lead the row (no
            wordmark, no count badges). Active verb gets a hairline
            cream-gold underbar. Right cluster: ⌘K + Tour + AI orb +
            avatar. The container padding-right is overridden inline
            so the cluster sits closer to the viewport edge. */}
        <div
          className="container flex items-center gap-3 py-2.5 sm:gap-4 sm:py-3"
          style={{ paddingRight: "clamp(8px, 1vw, 16px)" }}
        >
          {/* Primary verbs in italic Fraunces - give the nav real
              identity. The verbs ARE the brand now; no wordmark needed. */}
          <nav className="app-primary-nav flex min-w-0 flex-1 items-center gap-2.5 sm:gap-7">
            {(
              [
                // Daily verbs - neutral cream text.
                ["/today",   "Today",   "verb"],
                ["/lookup",  "Lookup",  "verb"],
                ["/records", "People",  "verb"],
                ["/stats",   "Stats",   "verb"],
                // How To is the staff handbook - different category, so
                // it gets the cream-gold accent color to read as
                // distinct from the daily verbs without leaving the row.
                // Story moved to the avatar dropdown (less-frequent).
                ["/how-to",  "How To",  "ref"],
              ] as const
            ).map(([to, label, kind]) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `app-primary-nav-link relative flex-shrink-0 px-3 py-1 transition ${
                    isActive
                      ? "text-[#F2E9D6]"
                      : "text-[color:var(--color-text-dim)] hover:text-[color:var(--color-text-muted)]"
                  }`
                }
                data-nav-to={to}
                style={({ isActive }) => ({
                  fontFamily: "var(--font-display)",
                  fontStyle: "italic",
                  fontWeight: isActive ? 600 : 500,
                  fontSize: "clamp(12.5px, 3.4vw, 19px)",
                  fontVariationSettings: '"opsz" 36, "SOFT" 50',
                  letterSpacing: "-0.005em",
                  whiteSpace: "nowrap",
                  background: isActive ? "#8C1F2F" : "transparent",
                  borderRadius: 8,
                })}
              >
                {({ isActive }) => (
                  <>
                    <span>{label}</span>
                    {isActive && (
                      <span
                        aria-hidden
                        style={{
                          position: "absolute",
                          left: 8,
                          right: 8,
                          bottom: 2,
                          height: 2,
                          background: "#D6BE7A",
                          borderRadius: 2,
                        }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Right cluster - utilities collapsed into avatar menu. */}
          <div className="app-header-actions flex flex-shrink-0 items-center gap-2 sm:gap-3">
            {/* ⌘K trigger - bigger now + animated rainbow gradient
                stroke around the pill so the eye lands on it. Click
                opens the universal command palette. The text uses the
                same gradient via background-clip on a <span>. */}
            <button
              type="button"
              onClick={() => openCommandPalette()}
              title="Open search · ⌘K"
              aria-label="Open command palette"
              className="tap-bump hidden sm:inline-flex items-center kk-rainbow-pill"
            >
              <span className="kk-rainbow-text">⌘K</span>
            </button>



            {/* Take Tour link - sits next to ⌘K. Same attention-grabbing
                idea but in a different palette (cool teal-mint shimmer)
                so the two read as paired discoverability hints, not a
                competing pair. */}
            <button
              type="button"
              onClick={() => startTour()}
              data-tour="take-tour-button"
              title="Take the guided tour"
              aria-label="Take the guided tour"
              className="tap-bump hidden sm:inline-flex items-center tour-rainbow-pill"
            >
              <span className="tour-rainbow-text">Tour</span>
            </button>

            {/* Mobile-only icon search - kbd shortcut isn't available on
                touch, so a tappable target stays. */}
            <button
              type="button"
              onClick={() => openCommandPalette()}
              title="Search"
              aria-label="Open command palette"
              className="tap-bump rounded p-1.5 text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-bg-card-hi)] hover:text-[color:var(--color-text)] sm:hidden"
              style={{ minWidth: 32, minHeight: 32 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.3-4.3" />
              </svg>
            </button>

            {/* AI assistant orb - clickable, opens NJRC-scoped chat. */}
            <NavAssistantOrb
              onClick={() => {
                dispatchDamonBark("help-open");
                setAssistantOpen(true);
              }}
              ariaLabel="Open NJRC AI assistant"
            />

            <button
              type="button"
              onClick={toggleMute}
              title={muted ? "Unmute Damon" : "Mute Damon"}
              aria-label={muted ? "Unmute Damon narration" : "Mute Damon narration"}
              className="tap-bump inline-grid place-items-center rounded text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-bg-card-hi)] hover:text-[color:var(--color-text)]"
              style={{ width: 32, height: 32 }}
            >
              {muted ? <VolumeX size={16} strokeWidth={2} /> : <Volume2 size={16} strokeWidth={2} />}
            </button>

            <MusicPlayer music={music} />

            {/* Avatar menu - absorbs Live status, Sound, Music, Take tour,
                How to, More items, Sign out. Active-job count surfaces
                here too as a small dot indicator. */}
            <AvatarMenu
              username={user.login}
              isAdmin={user.role === "admin"}
              connected={connected}
              activeCount={activeCount}
              muted={muted}
              onToggleMute={toggleMute}
              music={music}
              tourProminent={tourProminent}
              onLogout={handleLogout}
            />
          </div>
        </div>
      </header>

      {/* Page content. .page-shell on each page applies its own gutter,
          so main has zero padding here.

          Top-level ErrorBoundary: previously a render-time throw in any
          page (the 2026-05-18 case was StatusPill crashing on an
          unrecognized "canceled" status) would unmount the entire React
          tree, leaving only the browser tab title visible. Now those
          show the boundary's notice instead of going black. Keyed on
          pathname so navigating to a different page resets a tripped
          boundary without a full reload. */}
      <main className="fade-in">
        <ErrorBoundary key={location.pathname} label={`page:${location.pathname}`}>
          <Outlet />
        </ErrorBoundary>
      </main>

      {/* Universal ⌘K / "/" command palette - mounted globally so search
          works from anywhere in the app. Searches people, How-to topics,
          categories, and quick navigation actions. The component is
          internally controlled and renders nothing when closed. */}
      <CommandPalette />

      {/* Global download/action toaster - bottom-right notifications
          for long-running actions (PDF generation, packet builds,
          etc.). Spinner while in flight, checkmark on success, error
          on failure. Used by the FloatingDock action menu so a click
          on "Generate PDF" gives instant feedback instead of feeling
          laggy. Auto-dismisses on success after ~1.8s, holds on
          error. */}
      <DownloadToaster />

      {/* NJRC AI assistant - opened by the nav orb. Reuses the same
          /api/how-to/chat endpoint (already NJRC-scoped: refuses
          off-topic, cites LSM 2026 topics) so the assistant
          experience is consistent everywhere it's invoked. */}
      <HowToChatbot
        open={assistantOpen}
        onClose={() => {
          setAssistantOpen(false);
          setAssistantTopicId(undefined);
          setAssistantInitialQuery(undefined);
        }}
        contextTopicId={assistantTopicId}
        initialQuery={assistantInitialQuery}
        initialAutoSend={!!assistantInitialQuery}
      />

      {/* Guided tour. Persistent overlay; auto-launches on first-ever
          login (no completedAt + no dismissedAt in localStorage), can
          be re-triggered any time via startTour(). The component
          renders nothing when not active. */}
      <Tour />

      {/* Soft contemplative coda - one rotating zen-ish line about
          fucking up. Re-rolls per route navigation. */}
      <FooterQuote />

      <footer className="border-t border-[color:var(--color-line)] mt-12 no-print">
        <div className="container py-6 meta flex items-center justify-between">
          <span>NJRC Court Records · NJ Municipal Court Case Search</span>
          <span>
            Press <kbd className="mx-1 rounded border border-[color:var(--color-line)] bg-[color:var(--color-bg-card)] px-1.5 py-0.5 font-mono text-[10px]">⌘K</kbd>
            to search people, topics, and actions
          </span>
        </div>
      </footer>
      <Toaster />
    </div>
  );
}


/**
 * "Ask Jack" button in the top nav. Compact pill with a live blue orb
 * (small CSS rendition of the chat modal's particle orb) and a label.
 * The orb has multiple layered animations (core breath, dual halo
 * pulse, drifting outer aura) so it reads as alive even at 28px.
 * Hover bumps the halo + glow + pill border for clear affordance.
 */
function NavAssistantOrb({
  onClick,
  ariaLabel,
}: {
  onClick: () => void;
  ariaLabel: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title="Ask Jack - your NJRC AI assistant (⌘J)"
      className="tap-bump nav-jack-pill"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        // Padding shrinks on small screens (where the "Ask Jack"
        // label is hidden) so the pill becomes a tidy round badge,
        // freeing horizontal room for the verb row.
        padding: "4px 12px 4px 6px",
        borderRadius: 999,
        cursor: "pointer",
        fontFamily: "var(--font-display)",
        fontStyle: "italic",
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: "-0.005em",
        lineHeight: 1,
        whiteSpace: "nowrap",
        transition: "background 200ms ease, border-color 200ms ease, color 200ms ease",
      }}
    >
      <span
        aria-hidden
        className="nav-jack-orb"
        style={{
          position: "relative",
          width: 22,
          height: 22,
          flex: "0 0 auto",
        }}
      >
        {/* Outer drifting halo */}
        <span
          aria-hidden
          className="nav-jack-halo"
          style={{
            position: "absolute",
            inset: -3,
            borderRadius: 999,
            background:
              "radial-gradient(circle at 50% 50%, rgba(214, 190, 122, 0.38) 0%, rgba(140, 31, 47, 0.14) 45%, rgba(140, 31, 47, 0) 74%)",
          }}
        />
        {/* Mid ring */}
        <span
          aria-hidden
          className="nav-jack-ring"
          style={{
            position: "absolute",
            inset: -2,
            borderRadius: 999,
            border: "1px solid rgba(214, 190, 122, 0.48)",
            opacity: 0.55,
          }}
        />
        {/* Core sphere */}
        <span
          aria-hidden
          className="nav-jack-core"
          style={{
            position: "absolute",
            inset: 3,
            borderRadius: 999,
            background:
              "radial-gradient(circle at 32% 28%, #FFF7D6 0%, #D6BE7A 30%, #A02C44 72%, #5B1322 100%)",
            boxShadow:
              "0 0 5px rgba(214, 190, 122, 0.38), inset 0 0 4px rgba(255, 255, 255, 0.30)",
          }}
        />
      </span>
      <span className="nav-jack-label">Ask Jack</span>
      <style>{`
        .nav-jack-core { animation: nav-jack-breathe 2.4s ease-in-out infinite; }
        .nav-jack-halo { animation: nav-jack-halo 3.2s ease-in-out infinite; }
        .nav-jack-ring { animation: nav-jack-ring 2.8s ease-in-out infinite; }
        /* On phones the orb itself is the affordance - hide the
           "Ask Jack" label and shrink the pill to a circle so it
           stops eating into the verb row. */
        @media (max-width: 639px) {
          .nav-jack-pill { padding: 4px !important; }
          .nav-jack-label { display: none; }
        }
        @keyframes nav-jack-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.10); }
        }
        @keyframes nav-jack-halo {
          0%, 100% { opacity: 0.42; transform: scale(1); }
          50%      { opacity: 0.76; transform: scale(1.12); }
        }
        @keyframes nav-jack-ring {
          0%, 100% { opacity: 0.45; transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(1.10); }
        }
        /* Default (dark theme) chrome lives here so it can be overridden
           by [data-theme="light"] .nav-jack-pill in index.css. Inline
           style on the button only sets layout/typography - color comes
           from CSS so themes can vary it. */
        .nav-jack-pill {
          background: rgba(214, 190, 122, 0.10);
          border: 1px solid rgba(214, 190, 122, 0.34);
          color: #F2E9D6;
        }
        .nav-jack-pill:hover {
          background: rgba(214, 190, 122, 0.16);
          border-color: rgba(214, 190, 122, 0.58);
          color: #FFF7D6;
        }
        .nav-jack-pill:hover .nav-jack-halo { opacity: 0.82; transform: scale(1.16); }
        @media (prefers-reduced-motion: reduce) {
          .nav-jack-core, .nav-jack-halo, .nav-jack-ring { animation: none; }
        }
      `}</style>
    </button>
  );
}

/**
 * Single dropdown menu that absorbs everything we previously stuffed into
 * the second nav row + utility cluster: connection status, active-scrape
 * count, sound toggle, music toggle, Take tour, How to, More items,
 * Sign out. Trigger is an avatar disc showing the user's initial; an
 * orange pulse appears on the disc whenever activeCount > 0.
 */
function AvatarMenu({
  username,
  isAdmin,
  connected,
  activeCount,
  muted,
  onToggleMute,
  music,
  tourProminent,
  onLogout,
}: {
  username: string;
  isAdmin: boolean;
  connected: boolean;
  activeCount: number;
  muted: boolean;
  onToggleMute: () => void;
  music: ReturnType<typeof useMusic>;
  tourProminent: boolean;
  onLogout: () => void;
}): JSX.Element {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-avatar-menu]")) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Close after navigation
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const initial = (username[0] ?? "?").toUpperCase();

  // More-menu items, mirrored from the prior MoreMenu component.
  const moreItems: Array<[string, string, boolean?]> = [
    ["/stats", "Stats"],
    ["/how-to", "How To"],
    ["/ideas", "Ideas"],
    ["/log", "Log"],
    ["/admin/scrape-health", "Scrape"],
    ...(isAdmin ? ([["/admin/users", "Manage users"]] as Array<[string, string, boolean?]>) : []),
    ["/sources", "Sources", true],
    ["/battle", "Arena"],
    ["/life-quiz", "Life Quiz"],
  ];

  const toggleMusic = () => {
    if (!music.enabled) { music.enable(); return; }
    if (music.isPlaying) music.pause();
    else { music.resume(); music.ensurePlaying(); }
  };

  return (
    <div data-avatar-menu className="relative">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-haspopup="menu"
        aria-expanded={open}
        title={username}
        className="tap-bump relative grid place-items-center"
        style={{
          width: 34,
          height: 34,
          borderRadius: 999,
          border: "1px solid var(--color-line-hi)",
          background: "var(--color-bg-card)",
          color: "var(--color-text)",
          fontFamily: "var(--font-mono)",
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: "0.04em",
        }}
      >
        {initial}
        {activeCount > 0 && (
          <span
            aria-hidden
            className="dot dot-pulse"
            style={{
              position: "absolute",
              top: -2,
              right: -2,
              width: 8,
              height: 8,
              background: "var(--color-orange-hi)",
            }}
          />
        )}
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            // Themed surface - picks up dark card in dark mode and pure
            // white in light mode. The 97% opacity + backdrop-blur keeps
            // the frosted-popover feel without hardcoding a color.
            background: "color-mix(in oklab, var(--color-bg-card) 97%, transparent)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            color: "var(--color-text)",
            border: "1px solid var(--color-line-hi)",
            borderRadius: 10,
            padding: 6,
            minWidth: 220,
            boxShadow: "0 14px 40px color-mix(in oklab, var(--color-text) 30%, transparent)",
            zIndex: 50,
          }}
        >
          {/* Identity row */}
          <div
            style={{
              padding: "8px 12px 10px",
              borderBottom: "1px solid var(--color-line)",
              marginBottom: 4,
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--color-text-dim)",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              Signed in
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>
              {username}
            </div>
            <div
              style={{
                marginTop: 6,
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
                color: "var(--color-text-muted)",
              }}
            >
              <span
                className="dot"
                style={{ background: connected ? "var(--color-sage-hi)" : "var(--color-text-dim)" }}
              />
              {connected ? "Live" : "Disconnected"}
              {activeCount > 0 && (
                <span style={{ color: "var(--color-orange-hi)" }}>
                  · {activeCount} active scrape{activeCount === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </div>

          {/* Toggles */}
          <ThemeToggleRow />
          <MenuToggleRow
            label={muted ? "Damon - muted" : "Damon - on"}
            onClick={onToggleMute}
            on={!muted}
          />
          <MenuToggleRow
            label={music.isPlaying ? "Music - playing" : music.enabled ? "Music - paused" : "Music - off"}
            onClick={toggleMusic}
            on={music.isPlaying}
          />

          <div style={{ height: 1, background: "var(--color-line)", margin: "6px 0" }} />

          {/* Take tour - accent-styled when prominent */}
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); startTour(); }}
            data-tour="take-tour-button"
            className="block w-full text-left rounded transition"
            style={{
              padding: "8px 12px",
              fontSize: 13,
              color: tourProminent ? "var(--color-accent)" : "var(--color-text-muted)",
              fontWeight: tourProminent ? 600 : 500,
            }}
          >
            Take the guided tour
          </button>

          {/* Story moved here from the main verb row - it's a nice-to-
              have, not a daily verb. How To is now in the main nav (cream-
              gold accent) so it's no longer in this dropdown. */}
          <NavLink
            to="/story"
            role="menuitem"
            className={({ isActive }) =>
              `block rounded transition ${
                isActive
                  ? "text-[color:var(--color-text)] bg-[color:var(--color-bg-card-hi)]"
                  : "text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)] hover:bg-[color:var(--color-bg-card-hi)]"
              }`
            }
            style={{ padding: "8px 12px", fontSize: 13 }}
          >
            Story
          </NavLink>

          <div style={{ height: 1, background: "var(--color-line)", margin: "6px 0" }} />

          {/* More items */}
          {moreItems.map(([to, label, isExternal]) =>
            isExternal ? (
              <a
                key={to}
                href={to}
                role="menuitem"
                className="block rounded transition text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)] hover:bg-[color:var(--color-bg-card-hi)]"
                style={{ padding: "8px 12px", fontSize: 13 }}
              >
                {label}
              </a>
            ) : (
              <NavLink
                key={to}
                to={to}
                role="menuitem"
                className={({ isActive }) =>
                  `block rounded transition ${
                    isActive
                      ? "text-[color:var(--color-text)] bg-[color:var(--color-bg-card-hi)] font-semibold"
                      : "text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)] hover:bg-[color:var(--color-bg-card-hi)]"
                  }`
                }
                style={{ padding: "8px 12px", fontSize: 13 }}
              >
                {label}
              </NavLink>
            ),
          )}

          <div style={{ height: 1, background: "var(--color-line)", margin: "6px 0" }} />

          <button
            type="button"
            role="menuitem"
            onClick={onLogout}
            className="block w-full text-left rounded transition text-[color:var(--color-text-muted)] hover:text-[color:var(--color-rose-hi)] hover:bg-[color:var(--color-bg-card-hi)]"
            style={{ padding: "8px 12px", fontSize: 13 }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function MusicPlayer({ music }: { music: ReturnType<typeof useMusic> }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const modeLabels: Record<MusicMode, string> = {
    focus: "Focus",
    review: "Review",
    "after-hours": "After hours",
  };
  const playPause = () => {
    if (music.isPlaying) music.pause();
    else if (music.enabled) music.resume();
    else music.enable();
  };
  const volumePct = Math.round(music.volume * 100);
  return (
    <div
      className={`music-player ${expanded ? "is-expanded" : ""}`}
      data-playing={music.isPlaying ? "true" : "false"}
      data-muted={music.muted ? "true" : "false"}
    >
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="tap-bump music-player-mobile-toggle"
        aria-label="Open music player"
        title="Music player"
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M9 18V5l12-2v13" />
          <circle cx="6" cy="18" r="3" />
          <circle cx="18" cy="16" r="3" />
        </svg>
      </button>
      <div className="music-player-shell">
        <button
          type="button"
          className="music-player-now"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={`Music player. ${music.currentTrackTitle}. ${modeLabels[music.mode]}`}
          title={`${music.currentTrackTitle} · ${modeLabels[music.mode]}`}
        >
          <span className="music-eq" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <span className="music-player-now-state" aria-hidden="true">
            {music.isPlaying ? (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M7 5h4v14H7z" />
                <path d="M13 5h4v14h-4z" />
              </svg>
            ) : (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </span>
        </button>
      </div>
      {expanded && (
        <div className="music-player-panel">
          <div className="music-player-transport" role="group" aria-label="Music transport">
            <button
              type="button"
              onClick={music.previousTrack}
              className="tap-bump music-player-icon-btn"
              aria-label="Previous track"
              title="Previous track"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M6 5h2v14H6z" />
                <path d="M19 6.4v11.2L10.2 12 19 6.4z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={playPause}
              className="tap-bump music-player-play-btn"
              aria-label={music.isPlaying ? "Pause music" : "Play music"}
              title={music.isPlaying ? "Pause music" : "Play music"}
            >
              {music.isPlaying ? (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M7 5h4v14H7z" />
                  <path d="M13 5h4v14h-4z" />
                </svg>
              ) : (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={music.nextTrack}
              className="tap-bump music-player-icon-btn"
              aria-label="Next track"
              title="Next track"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M16 5h2v14h-2z" />
                <path d="M5 6.4 13.8 12 5 17.6V6.4z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={music.toggleMute}
              className="tap-bump music-player-icon-btn"
              aria-label={music.muted ? "Unmute music" : "Mute music"}
              title={music.muted ? "Unmute music" : "Mute music"}
            >
              {music.muted || music.volume <= 0 ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M11 5 6 9H3v6h3l5 4V5Z" />
                  <path d="m19 9-6 6" />
                  <path d="m13 9 6 6" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M11 5 6 9H3v6h3l5 4V5Z" />
                  <path d="M15.5 8.5a5 5 0 0 1 0 7" />
                </svg>
              )}
            </button>
          </div>
          <div className="music-player-modes" role="group" aria-label="Music mode">
          {(["focus", "review", "after-hours"] as MusicMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => music.setMode(mode)}
              className="music-player-mode"
              data-active={music.mode === mode ? "true" : "false"}
            >
              {modeLabels[mode]}
            </button>
          ))}
          </div>
          <div className="music-player-volume">
            <button
              type="button"
              onClick={music.toggleMute}
              className="music-player-volume-mute"
              aria-label={music.muted ? "Unmute music" : "Mute music"}
            >
              {music.muted || volumePct === 0 ? "Muted" : "Volume"}
            </button>
            <input
              type="range"
              min="0"
              max="100"
              value={volumePct}
              onChange={(e) => music.setVolume(Number(e.currentTarget.value) / 100)}
              aria-label="Music volume"
            />
            <span className="music-player-volume-value">{volumePct}%</span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * One-line toggle row for the avatar menu. `on` switches the dot color.
 */
// Two-state theme switch - sun/moon icon trades sides depending on the
// active theme. Lives at the top of the AvatarMenu's toggle stack.
function ThemeToggleRow(): JSX.Element {
  const { theme, toggle } = useTheme();
  const isLight = theme === "light";
  return (
    <button
      type="button"
      role="menuitem"
      onClick={toggle}
      title={isLight ? "Switch to dark theme" : "Switch to light theme"}
      className="block w-full text-left rounded transition text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)] hover:bg-[color:var(--color-bg-card-hi)]"
      style={{
        padding: "8px 12px",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 14,
          height: 14,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: isLight ? "var(--color-amber-hi)" : "var(--color-blue-hi)",
        }}
      >
        {isLight ? (
          // Sun
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
          </svg>
        ) : (
          // Moon
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        )}
      </span>
      <span style={{ flex: 1 }}>
        Theme
        <span style={{ color: "var(--color-text-dim)", marginLeft: 6, fontSize: 11 }}>
          · {isLight ? "Light" : "Dark"}
        </span>
      </span>
      <span
        aria-hidden
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--color-text-dim)",
          letterSpacing: "0.1em",
        }}
      >
        switch
      </span>
    </button>
  );
}

function MenuToggleRow({
  label,
  onClick,
  on,
}: {
  label: string;
  onClick: () => void;
  on: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="block w-full text-left rounded transition text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)] hover:bg-[color:var(--color-bg-card-hi)]"
      style={{
        padding: "8px 12px",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span
        aria-hidden
        className="dot"
        style={{
          background: on ? "var(--color-sage-hi)" : "var(--color-text-dim)",
          width: 7,
          height: 7,
        }}
      />
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  );
}
